import { useApiClient } from '@/composables/useApiClient'
import type { IPaginationResponse } from '@/services/interfaces/pagination-response.interface'

export type NoteModuleType = 'HUB' | 'OPER'

export interface Note {
  id: string
  title: string
  content: string
  number?: string | null
  user_id?: string | null
  color?: string | null
  is_pinned: boolean
  extension_id?: string | null
  type: NoteModuleType
  created_at: string
  updated_at: string
  position?: number
}

export interface CreateNotePayload {
  title: string
  content: string
  number?: string | null
  user_id?: string | null
  color?: string | null
  is_pinned?: boolean
  extension_id?: string | null
  type?: NoteModuleType
}

export type UpdateNotePayload = Partial<CreateNotePayload>

export interface NoteFilters {
  title?: string
  number?: string
  user_id?: string
  extension_id?: string
  type?: NoteModuleType
  is_pinned?: boolean
  page?: number
  size?: number
}

/** Tope de `size` que acepta GET /oper/notes (Query(..., le=100)). */
const NOTES_PAGE_SIZE = 100

/** Cota de seguridad: 50 paginas = 5000 notas. Evita un bucle sin fin si `meta` viene raro. */
const NOTES_MAX_PAGES = 50

export interface AllNotesResult {
  notes: Note[]
  /** Total que reporta el backend; mayor que `notes.length` solo si se corto por NOTES_MAX_PAGES. */
  total: number
  truncated: boolean
}

export const useNotesService = () => {
  const privateApi = useApiClient(true)

  const getNotes = async (
    filters?: NoteFilters,
  ): Promise<IPaginationResponse<Note>> => {
    return await privateApi.get<IPaginationResponse<Note>>('/oper/notes', {
      params: filters,
    })
  }

  /**
   * Recorre todas las paginas y devuelve las notas juntas.
   *
   * El panel ordena por drag & drop y busca en cliente sobre la lista completa,
   * asi que necesita todas las notas cargadas: con una sola pagina de 100 un
   * agente con mas notas no veia el resto ni podia buscarlas.
   */
  const getAllNotes = async (
    filters?: Omit<NoteFilters, 'page' | 'size'>,
  ): Promise<AllNotesResult> => {
    const first = await getNotes({ ...filters, page: 1, size: NOTES_PAGE_SIZE })
    const notes = [...first.data]
    const total = first.meta?.total ?? notes.length
    const pages = Math.min(first.meta?.pages ?? 1, NOTES_MAX_PAGES)

    if (pages > 1) {
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, index) =>
          getNotes({ ...filters, page: index + 2, size: NOTES_PAGE_SIZE }),
        ),
      )
      rest.forEach((response) => notes.push(...response.data))
    }

    return { notes, total, truncated: notes.length < total }
  }

  const createNote = async (payload: CreateNotePayload): Promise<Note> => {
    return await privateApi.post<Note, CreateNotePayload>('/oper/notes', payload)
  }

  const updateNote = async (noteId: string, payload: UpdateNotePayload): Promise<Note> => {
    return await privateApi.patch<Note, UpdateNotePayload>(`/oper/notes/${noteId}`, payload)
  }

  const deleteNote = async (noteId: string): Promise<Note> => {
    return await privateApi.delete<Note>(`/oper/notes/${noteId}`)
  }

  return {
    getNotes,
    getAllNotes,
    createNote,
    updateNote,
    deleteNote,
  }
}
